import { getSupportUnreadCount, markSupportAsRead } from './supportApi';

type UnreadUpdateCallback = (count: number) => void;

class NotificationService {
    private static instance: NotificationService;
    private callbacks: UnreadUpdateCallback[] = [];
    private currentUnreadCount: number = 0;
    private telegramId: string | null = null;
    private intervalId: NodeJS.Timeout | null = null;
    private isPolling: boolean = false;
    private isFetching: boolean = false;

    private constructor() {}

    static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    init(telegramId: string | null) {
        if (this.telegramId === telegramId) return;

        this.telegramId = telegramId;

        if (telegramId) {
            this.startPolling();
        } else {
            this.stopPolling();
        }
    }

    private startPolling() {
        if (this.isPolling) return;

        this.isPolling = true;
        this.fetchUnreadCount();

        this.intervalId = setInterval(() => {
            if (document.visibilityState === 'hidden') {
                return;
            }

            this.fetchUnreadCount();
        }, 30000);
    }

    private stopPolling() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.isPolling = false;
        this.isFetching = false;
    }

    private async fetchUnreadCount() {
        if (!this.telegramId || this.isFetching) return;

        this.isFetching = true;
        try {
            const response = await getSupportUnreadCount(this.telegramId);
            if (response.status === 'success') {
                const newCount = response.unread_count;
                if (this.currentUnreadCount !== newCount) {
                    this.currentUnreadCount = newCount;
                    this.notifyAll(newCount);
                }
            }
        } catch (error) {
        } finally {
            this.isFetching = false;
        }
    }

    async refresh() {
        await this.fetchUnreadCount();
    }

    async markAsRead() {
        if (!this.telegramId) return;

        try {
            await markSupportAsRead(this.telegramId);
            this.currentUnreadCount = 0;
            this.notifyAll(0);
            setTimeout(() => this.fetchUnreadCount(), 500);
        } catch (error) {
        }
    }

    subscribe(callback: UnreadUpdateCallback): () => void {
        this.callbacks.push(callback);
        callback(this.currentUnreadCount);

        return () => {
            const index = this.callbacks.indexOf(callback);
            if (index > -1) {
                this.callbacks.splice(index, 1);
            }
        };
    }

    private notifyAll(count: number) {
        this.callbacks.forEach(callback => {
            try {
                callback(count);
            } catch (error) {
            }
        });
    }

    getCurrentCount(): number {
        return this.currentUnreadCount;
    }

    clear() {
        this.stopPolling();
        this.callbacks = [];
        this.currentUnreadCount = 0;
        this.telegramId = null;
    }
}

export const notificationService = NotificationService.getInstance();
